/**
 * Customer Groups Hook
 * React hook ügyfélcsoportok kezeléséhez
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CustomerGroup, CustomerGroupStats } from '../types/customerGroup';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  getCustomerGroups,
  getCustomerGroup,
  createCustomerGroup,
  updateCustomerGroup,
  deleteCustomerGroup,
  addCustomersToGroup,
  removeCustomersFromGroup,
  getCustomerGroupStats,
  updateGroupUsage,
  exportCustomerGroup
} from '../services/customerGroupService';

export function useCustomerGroups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<CustomerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for groups
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const groupsRef = collection(db, 'customerGroups');
    const q = query(groupsRef, where('organizationId', '==', user.organizationId));

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const groupsData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as CustomerGroup[];
        
        setGroups(groupsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Hiba a csoportok betöltése során');
        console.error('Error loading customer groups:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId]);

  // Csoportok betöltése (manual refresh)
  const loadGroups = useCallback(async () => {
    if (!user?.organizationId) {
      return;
    }

    try {
      const data = await getCustomerGroups(user.organizationId);
      setGroups(data);
    } catch (err: any) {
      setError(err.message || 'Hiba a csoportok betöltése során');
      console.error('Error loading customer groups:', err);
    }
  }, [user?.organizationId]);

  // Új csoport létrehozása
  const create = useCallback(async (
    group: Omit<CustomerGroup, 'id' | 'createdAt' | 'updatedAt' | 'customerCount' | 'organizationId' | 'createdBy'>
  ): Promise<string> => {
    if (!user?.organizationId || !user?.uid) {
      throw new Error('User not authenticated');
    }

    const groupData = {
      ...group,
      organizationId: user.organizationId,
      createdBy: user.uid
    };

    const id = await createCustomerGroup(groupData);
    await loadGroups();
    return id;
  }, [user, loadGroups]);

  // Csoport frissítése
  const update = useCallback(async (
    groupId: string,
    updates: Partial<Omit<CustomerGroup, 'id' | 'createdAt' | 'organizationId' | 'createdBy'>>
  ): Promise<void> => {
    await updateCustomerGroup(groupId, updates);
    await loadGroups();
  }, [loadGroups]);

  // Csoport törlése
  const remove = useCallback(async (groupId: string): Promise<void> => {
    await deleteCustomerGroup(groupId);
    await loadGroups();
  }, [loadGroups]);

  // Ügyfelek hozzáadása
  const addCustomers = useCallback(async (
    groupId: string,
    customerIds: string[]
  ): Promise<void> => {
    await addCustomersToGroup(groupId, customerIds);
    // Real-time listener automatically updates
  }, []);

  // Ügyfelek eltávolítása
  const removeCustomers = useCallback(async (
    groupId: string,
    customerIds: string[]
  ): Promise<void> => {
    await removeCustomersFromGroup(groupId, customerIds);
    // Real-time listener automatically updates
  }, []);

  // Csoport lekérése ID alapján
  const getById = useCallback(async (groupId: string): Promise<CustomerGroup | null> => {
    return await getCustomerGroup(groupId);
  }, []);

  // Statisztika lekérése
  const getStats = useCallback(async (groupId: string): Promise<CustomerGroupStats | null> => {
    if (!user?.organizationId) return null;
    return await getCustomerGroupStats(groupId, user.organizationId);
  }, [user?.organizationId]);

  // Használat frissítése
  const markAsUsed = useCallback(async (groupId: string): Promise<void> => {
    await updateGroupUsage(groupId);
    await loadGroups();
  }, [loadGroups]);

  // Export
  const exportGroup = useCallback(async (groupId: string): Promise<any[]> => {
    if (!user?.organizationId) {
      throw new Error('User not authenticated');
    }
    return await exportCustomerGroup(groupId, user.organizationId);
  }, [user?.organizationId]);

  return {
    groups,
    loading,
    error,
    create,
    update,
    remove,
    addCustomers,
    removeCustomers,
    getById,
    getStats,
    markAsUsed,
    exportGroup,
    refresh: loadGroups
  };
}

// Hook egy konkrét csoport lekérésére
export function useCustomerGroup(groupId: string | null) {
  const [group, setGroup] = useState<CustomerGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      setLoading(false);
      return;
    }

    const loadGroup = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCustomerGroup(groupId);
        setGroup(data);
      } catch (err: any) {
        setError(err.message || 'Hiba a csoport betöltése során');
        console.error('Error loading customer group:', err);
      } finally {
        setLoading(false);
      }
    };

    loadGroup();
  }, [groupId]);

  return { group, loading, error };
}
